import { apiGet, apiPut } from '@/api/http';
import { isRecord } from '@/utils/typeGuards';

import type { DataTableLayout } from '@/UI/DataTable/types';

const apiUrl = import.meta.env.VITE_API_URL;

export type TablePreference = DataTableLayout & {
  tableId: string;
  schemaVersion: number;
  pageSize: number;
  updatedAt: string;
};

type TablePreferenceResponse = {
  table_id: string;
  schema_version: number;
  widths: Record<string, number>;
  order: string[];
  hidden: string[];
  page_size: number;
  updated_at: string;
};

const mapPreference = (
  preference: TablePreferenceResponse,
): TablePreference => ({
  tableId: preference.table_id,
  schemaVersion: preference.schema_version,
  widths: preference.widths,
  order: preference.order,
  hidden: preference.hidden,
  pageSize: preference.page_size,
  updatedAt: preference.updated_at,
});

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}

function isNumberRecord(value: unknown): value is Record<string, number> {
  return (
    isRecord(value) &&
    Object.values(value).every((width) => typeof width === 'number')
  );
}

function isTablePreferenceResponse(
  value: unknown,
): value is TablePreferenceResponse {
  if (!isRecord(value)) return false;
  if (typeof value.table_id !== 'string') return false;
  if (typeof value.schema_version !== 'number') return false;
  if (!isStringArray(value.order)) return false;
  if (!isStringArray(value.hidden)) return false;
  if (!isNumberRecord(value.widths)) return false;
  if (typeof value.page_size !== 'number') return false;
  if (typeof value.updated_at !== 'string') return false;
  return true;
}

export async function getTablePreference(
  tableId: string,
  schemaVersion: number,
): Promise<TablePreference | null> {
  const preference = await apiGet<unknown>(
    `${apiUrl}table-preferences/${encodeURIComponent(tableId)}/?schema_version=${schemaVersion}`,
  );
  return isTablePreferenceResponse(preference)
    ? mapPreference(preference)
    : null;
}

export async function saveTablePreference(
  preference: Omit<TablePreference, 'updatedAt'>,
): Promise<TablePreference> {
  const response = await apiPut<TablePreferenceResponse>(
    `${apiUrl}table-preferences/${encodeURIComponent(preference.tableId)}/`,
    {
      schema_version: preference.schemaVersion,
      widths: preference.widths,
      order: preference.order,
      hidden: preference.hidden,
      page_size: preference.pageSize,
    },
  );
  return mapPreference(response);
}
